import "./NotesPage.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { auth, db, storage } from "../../../firebase";
import { analyzeNoteAudio } from "../../../utility/aiApi";
import useNoteAudioRecorder, { NOTE_AUDIO_MAX_BYTES } from "../../../utility/useNoteAudioRecorder";

const SAVE_DELAY_MS = 1200;
const ATTACHMENT_MAX_BYTES = 15 * 1024 * 1024;
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const TEXT_COLOR_PRESETS = ["#ffffff", "#62d9ff", "#35d787", "#facc15", "#ff7890", "#d946ef"];
const TEXT_SIZE_OPTIONS = [
  { label: "S", value: "2" },
  { label: "M", value: "3" },
  { label: "L", value: "5" },
  { label: "XL", value: "7" },
];

const emptyMeeting = {
  hasAudio: false,
  audioStoragePath: null,
  transcriptStoragePath: null,
  transcriptTextPreview: null,
  transcriptStatus: "none",
  aiSummaryStatus: "none",
  durationSeconds: null,
  language: "auto",
};

const emptyAiSummary = {
  summary: "",
  decisions: [],
  actionItems: [],
  keyPoints: [],
  openQuestions: [],
};

const getOrgName = (org) =>
  org?.organization || org?.orgName || org?.name || org?.displayName || "Organisation";

const stripHtml = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const sanitizeHtml = (html = "") =>
  String(html)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");

const formatDate = (value) => {
  const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short" }).format(date);
};

const formatTimer = (seconds) => {
  const safe = Math.max(0, Math.floor(seconds || 0));
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
};

const compactFilename = (value, fallback = "note.pdf") => {
  const normalized = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 90);
  return normalized || fallback;
};

const pdfTextReplacements = {
  "☐": "[ ]",
  "☑": "[x]",
  "☒": "[x]",
  "✓": "v",
  "✔": "v",
  "✕": "x",
  "✖": "x",
  "→": "->",
  "←": "<-",
  "•": "-",
  "…": "...",
  "–": "-",
  "—": "-",
  "“": '"',
  "”": '"',
  "‘": "'",
  "’": "'",
  "€": "EUR",
};

const toPdfSafeText = (text, font) => {
  const replaced = String(text || "").replace(
    /[☐☑☒✓✔✕✖→←•…–—“”‘’€]/g,
    (match) => pdfTextReplacements[match] || "",
  );
  if (!font) return replaced;
  return Array.from(replaced)
    .map((char) => {
      try {
        font.widthOfTextAtSize(char, 10);
        return char;
      } catch (error) {
        return "";
      }
    })
    .join("");
};

const wrapPdfText = (text, font, fontSize, maxWidth) => {
  const words = toPdfSafeText(text, font).split(/\s+/);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
      line = candidate;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const getClosestElement = (node, selector, root) => {
  const start = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  const element = start?.closest?.(selector);
  return element && root?.contains(element) ? element : null;
};

const buildSummaryHtml = (summary, t) => {
  const list = (items = []) =>
    items.length
      ? `<ul>${items.map((item) => `<li>${sanitizeHtml(item)}</li>`).join("")}</ul>`
      : "";
  const actions = Array.isArray(summary?.actionItems)
    ? summary.actionItems
        .map((item) => {
          const suffix = [item.owner, item.dueDate].filter(Boolean).join(" · ");
          return `<li>${sanitizeHtml(item.task || "")}${suffix ? ` <em>${sanitizeHtml(suffix)}</em>` : ""}</li>`;
        })
        .join("")
    : "";
  return sanitizeHtml(`
    <h2>${summary?.titleSuggestion || t("summary_preview_title")}</h2>
    <p>${summary?.summary || ""}</p>
    <h3>${t("key_points")}</h3>${list(summary?.keyPoints || [])}
    <h3>${t("decisions")}</h3>${list(summary?.decisions || [])}
    <h3>${t("actions")}</h3>${actions ? `<ul>${actions}</ul>` : ""}
    <h3>${t("questions")}</h3>${list(summary?.openQuestions || [])}
  `);
};

function NotesPage({ org }) {
  const { t } = useTranslation("notes");
  const alert = useAlert();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const saveTimerRef = useRef(null);
  const suppressSaveRef = useRef(false);
  const loadedNoteIdRef = useRef("");
  const savedSelectionRef = useRef(null);
  const [notes, setNotes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState(null);
  const [tagsInput, setTagsInput] = useState("");
  const [queryText, setQueryText] = useState("");
  const [sort, setSort] = useState("manual");
  const [tagFilter, setTagFilter] = useState("");
  const [draggedNoteId, setDraggedNoteId] = useState("");
  const [mobileView, setMobileView] = useState("list");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [mobileColorPanelOpen, setMobileColorPanelOpen] = useState(false);
  const [mobileSizePanelOpen, setMobileSizePanelOpen] = useState(false);
  const [saveState, setSaveState] = useState("saved");
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [summaryPreview, setSummaryPreview] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState("");
  const [analysisFailed, setAnalysisFailed] = useState(false);
  const analysisInFlightRef = useRef(false);
  const activeNoteRef = useRef(null);

  const orgId = org?.id;
  const userId = auth.currentUser?.uid || "";
  activeNoteRef.current = { orgId, noteId: selectedId };
  const isCurrentNote = (context) =>
    activeNoteRef.current?.orgId === context.orgId &&
    activeNoteRef.current?.noteId === context.noteId;

  const {
    starting: startingRecording,
    recording,
    seconds: recordingSeconds,
    start: startRecording,
    stop: stopRecording,
  } = useNoteAudioRecorder({
    orgId,
    noteId: selectedId,
    onRecorded: (audio) => {
      setRecordedAudio(audio);
      analyzeRecording(audio);
    },
    onError: (error) => {
      const key = ["NotAllowedError", "SecurityError"].includes(error?.name)
        ? "microphone_denied"
        : error?.name === "NotFoundError"
          ? "microphone_not_found"
          : ["recording_unavailable", "audio_empty", "recording_failed"].includes(error?.code)
            ? error.code
            : "recording_failed";
      alert.error(t(key));
    },
  });

  useEffect(() => {
    setSummaryPreview(null);
    setRecordedAudio(null);
    setAnalysisFailed(false);
  }, [orgId, selectedId]);

  useEffect(() => {
    if (!recordedAudio?.blob) {
      setRecordedAudioUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(recordedAudio.blob);
    setRecordedAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [recordedAudio?.blob]);

  const notesCollection = useMemo(() => {
    if (!orgId) return null;
    return collection(db, "Organizations", orgId, "Notes");
  }, [orgId]);

  useEffect(() => {
    if (!notesCollection) {
      setNotes([]);
      setSelectedId("");
      setDraft(null);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      notesCollection,
      (snapshot) => {
        const items = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => item.status !== "archived");
        setNotes(items);
        setSelectedId((current) => current || items[0]?.id || "");
      },
      () => alert.error(t("save_error")),
    );
    return () => unsubscribe();
  }, [notesCollection, alert, t]);

  useEffect(() => {
    const selected = notes.find((note) => note.id === selectedId);
    if (!selected) {
      loadedNoteIdRef.current = "";
      setDraft(null);
      if (editorRef.current) editorRef.current.innerHTML = "";
      return;
    }
    if (loadedNoteIdRef.current === selectedId) return;
    loadedNoteIdRef.current = selectedId;
    suppressSaveRef.current = true;
    const nextDraft = {
      title: selected.title || "",
      contentHtml: selected.contentHtml || "",
      contentText: selected.contentText || "",
      tags: Array.isArray(selected.tags) ? selected.tags : [],
      attachments: Array.isArray(selected.attachments) ? selected.attachments : [],
    };
    setDraft(nextDraft);
    setTagsInput(nextDraft.tags.join(", "));
    if (editorRef.current) editorRef.current.innerHTML = nextDraft.contentHtml;
    setSaveState("saved");
    window.setTimeout(() => {
      suppressSaveRef.current = false;
    }, 0);
  }, [notes, selectedId]);

  useEffect(() => {
    if (!draft || !selectedId || suppressSaveRef.current) return undefined;
    setSaveState("saving");
    window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      try {
        await updateDoc(doc(db, "Organizations", orgId, "Notes", selectedId), {
          title: draft.title || t("untitled"),
          contentHtml: sanitizeHtml(draft.contentHtml || ""),
          contentText: draft.contentText || stripHtml(draft.contentHtml || ""),
          tags: draft.tags,
          attachments: draft.attachments,
          updatedAt: serverTimestamp(),
          updatedBy: userId,
        });
        setSaveState("saved");
      } catch (error) {
        setSaveState("error");
        alert.error(t("save_error"));
      }
    }, SAVE_DELAY_MS);
    return () => window.clearTimeout(saveTimerRef.current);
  }, [draft, selectedId, orgId, userId, alert, t]);

  const allTags = useMemo(
    () => [...new Set(notes.flatMap((note) => (Array.isArray(note.tags) ? note.tags : [])))].sort(),
    [notes],
  );

  const filteredNotes = useMemo(() => {
    const text = queryText.trim().toLowerCase();
    return [...notes]
      .filter((note) => {
        if (tagFilter && !note.tags?.includes(tagFilter)) return false;
        if (!text) return true;
        return `${note.title || ""} ${note.contentText || ""}`.toLowerCase().includes(text);
      })
      .sort((a, b) => {
        if (sort === "title") return String(a.title || "").localeCompare(String(b.title || ""));
        if (sort === "manual") {
          const aOrder = Number.isFinite(Number(a.manualOrder))
            ? Number(a.manualOrder)
            : Number.MAX_SAFE_INTEGER;
          const bOrder = Number.isFinite(Number(b.manualOrder))
            ? Number(b.manualOrder)
            : Number.MAX_SAFE_INTEGER;
          if (aOrder !== bOrder) return aOrder - bOrder;
        }
        const aDate = a.updatedAt?.toMillis?.() || 0;
        const bDate = b.updatedAt?.toMillis?.() || 0;
        return bDate - aDate;
      });
  }, [notes, queryText, sort, tagFilter]);

  const createNote = async () => {
    if (!notesCollection || !orgId) return;
    const noteRef = doc(notesCollection);
    const nowTitle = t("untitled");
    const manualOrders = notes
      .map((note) => Number(note.manualOrder))
      .filter((value) => Number.isFinite(value));
    const nextManualOrder = manualOrders.length ? Math.min(...manualOrders) - 1 : 0;
    await setDoc(noteRef, {
      id: noteRef.id,
      orgId,
      title: nowTitle,
      contentHtml: "",
      contentText: "",
      tags: [],
      source: "manual",
      status: "active",
      manualOrder: nextManualOrder,
      attachments: [],
      meeting: emptyMeeting,
      aiSummary: emptyAiSummary,
      createdBy: userId,
      updatedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    setSelectedId(noteRef.id);
    setSort("manual");
    setMobileView("editor");
    return noteRef.id;
  };

  const updateDraft = (patch) => setDraft((current) => ({ ...(current || {}), ...patch }));

  const saveEditorSelection = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editorRef.current) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current.contains(range.commonAncestorContainer)) {
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const restoreEditorSelection = () => {
    if (!savedSelectionRef.current) return;
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(savedSelectionRef.current);
  };

  const exec = (command, value = null) => {
    editorRef.current?.focus();
    restoreEditorSelection();
    document.execCommand(command, false, value);
    handleEditorInput();
    saveEditorSelection();
  };

  const preserveSelectionPointer = (event) => {
    event.preventDefault();
    saveEditorSelection();
  };

  const setCaretAtStart = (element) => {
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    saveEditorSelection();
  };

  const setCaretAtEnd = (element) => {
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    saveEditorSelection();
  };

  const toggleHeading = () => {
    editorRef.current?.focus();
    restoreEditorSelection();
    const selection = window.getSelection();
    const anchor = selection?.anchorNode;
    const currentHeading = getClosestElement(anchor, "h2", editorRef.current);
    document.execCommand("formatBlock", false, currentHeading ? "p" : "h2");
    handleEditorInput();
    saveEditorSelection();
  };

  const insertChecklist = () => {
    exec("insertHTML", '<ul class="notes-checklist"><li>☐&nbsp;</li></ul>');
  };

  const exitChecklist = (list, item) => {
    const paragraph = document.createElement("p");
    paragraph.innerHTML = "<br>";
    list.insertAdjacentElement("afterend", paragraph);
    item.remove();
    if (!list.querySelector("li")) list.remove();
    setCaretAtStart(paragraph);
    handleEditorInput();
  };

  const addChecklistItemAfter = (item) => {
    const nextItem = document.createElement("li");
    nextItem.innerHTML = "☐&nbsp;";
    item.insertAdjacentElement("afterend", nextItem);
    setCaretAtEnd(nextItem);
    handleEditorInput();
  };

  const handleEditorKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    const selection = window.getSelection();
    const anchor = selection?.anchorNode;
    const list = getClosestElement(anchor, "ul.notes-checklist", editorRef.current);
    const item = getClosestElement(anchor, "li", list);
    if (!list || !item) return;

    event.preventDefault();
    const itemText = item.textContent.replace(/^[\s☐☑☒]+/, "").trim();
    if (!itemText) {
      exitChecklist(list, item);
      return;
    }
    addChecklistItemAfter(item);
  };

  const handleEditorInput = () => {
    const html = sanitizeHtml(editorRef.current?.innerHTML || "");
    updateDraft({ contentHtml: html, contentText: stripHtml(html) });
  };

  const handleTags = (value) => {
    setTagsInput(value);
    updateDraft({
      tags: value
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean)
        .slice(0, 20),
    });
  };

  const setTextColor = (color) => {
    if (!color) return;
    exec("foreColor", color);
  };

  const setTextSize = (size) => {
    if (!size) return;
    exec("fontSize", size);
  };

  const selectNote = (noteId) => {
    setSelectedId(noteId);
    setMobileView("editor");
    setActionsOpen(false);
    setMobileColorPanelOpen(false);
    setMobileSizePanelOpen(false);
  };

  const handleAction = (callback) => {
    setActionsOpen(false);
    callback();
  };

  const promptAndCreateLink = () => {
    const url = window.prompt("URL");
    if (url) exec("createLink", url);
  };

  const renderToolbarControls = (variant = "desktop") => (
    <>
      <button
        title={t("bold")}
        onPointerDown={preserveSelectionPointer}
        onClick={() => exec("bold")}
      >
        B
      </button>
      <button
        title={t("italic")}
        onPointerDown={preserveSelectionPointer}
        onClick={() => exec("italic")}
      >
        <em>I</em>
      </button>
      <button title={t("heading")} onPointerDown={preserveSelectionPointer} onClick={toggleHeading}>
        H2
      </button>
      {variant === "desktop" ? (
        <>
          <div className="notes-toolbar-group" title={t("text_color")}>
            <span>{t("text_color")}</span>
            {TEXT_COLOR_PRESETS.map((color) => (
              <button
                key={color}
                type="button"
                className="notes-color-dot"
                aria-label={`${t("text_color")} ${color}`}
                style={{ "--note-color": color }}
                onPointerDown={preserveSelectionPointer}
                onClick={() => setTextColor(color)}
              />
            ))}
            <input
              type="color"
              defaultValue="#ffffff"
              onMouseDown={saveEditorSelection}
              onChange={(event) => setTextColor(event.target.value)}
            />
          </div>
          <div className="notes-toolbar-group" title={t("text_size")}>
            <span>{t("text_size")}</span>
            {TEXT_SIZE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="notes-size-option"
                onPointerDown={preserveSelectionPointer}
                onClick={() => setTextSize(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            className={mobileColorPanelOpen ? "active" : ""}
            title={t("text_color")}
            onPointerDown={preserveSelectionPointer}
            onClick={() => {
              setMobileColorPanelOpen((value) => !value);
              setMobileSizePanelOpen(false);
            }}
          >
            A
          </button>
          <button
            type="button"
            className={mobileSizePanelOpen ? "active" : ""}
            title={t("text_size")}
            onPointerDown={preserveSelectionPointer}
            onClick={() => {
              setMobileSizePanelOpen((value) => !value);
              setMobileColorPanelOpen(false);
            }}
          >
            Tt
          </button>
        </>
      )}
      <button
        title={t("bullet_list")}
        onPointerDown={preserveSelectionPointer}
        onClick={() => exec("insertUnorderedList")}
      >
        •
      </button>
      <button
        title={t("numbered_list")}
        onPointerDown={preserveSelectionPointer}
        onClick={() => exec("insertOrderedList")}
      >
        1.
      </button>
      <button
        title={t("checklist")}
        onPointerDown={preserveSelectionPointer}
        onClick={insertChecklist}
      >
        ☑
      </button>
      <button
        title={t("link")}
        onPointerDown={preserveSelectionPointer}
        onClick={promptAndCreateLink}
      >
        🔗
      </button>
      <button
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => imageInputRef.current?.click()}
      >
        {variant === "mobile" ? "＋" : t("image")}
      </button>
      <button
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
      >
        {variant === "mobile" ? "📎" : t("attach")}
      </button>
    </>
  );

  const exportPdf = async () => {
    if (!draft) return;
    const pdfDoc = await PDFDocument.create();
    let page = pdfDoc.addPage([595.28, 841.89]);
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const margin = 56;
    const maxWidth = page.getWidth() - margin * 2;
    let y = page.getHeight() - margin;
    const drawLine = (text, options = {}) => {
      const fontSize = options.fontSize || 11;
      const usedFont = options.bold ? boldFont : font;
      const lines = wrapPdfText(text, usedFont, fontSize, maxWidth);
      lines.forEach((line) => {
        if (y < margin + 30) {
          page = pdfDoc.addPage([595.28, 841.89]);
          y = page.getHeight() - margin;
        }
        page.drawText(line, {
          x: margin,
          y,
          size: fontSize,
          font: usedFont,
          color: options.color || rgb(0.08, 0.12, 0.22),
        });
        y -= fontSize + 7;
      });
    };
    drawLine(draft.title || t("untitled"), { fontSize: 24, bold: true });
    y -= 8;
    drawLine(getOrgName(org), { fontSize: 10, color: rgb(0.38, 0.45, 0.58) });
    if (draft.tags?.length)
      drawLine(`#${draft.tags.join(" #")}`, { fontSize: 10, color: rgb(0.38, 0.45, 0.58) });
    y -= 16;
    stripHtml(draft.contentHtml || "")
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => drawLine(line, { fontSize: 11 }));
    const bytes = await pdfDoc.save();
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = compactFilename(`${draft.title || t("untitled")}.pdf`);
    link.click();
    URL.revokeObjectURL(url);
  };

  const reorderNotes = async (targetId) => {
    if (!draggedNoteId || !targetId || draggedNoteId === targetId || !orgId) return;
    const current = [...filteredNotes];
    const from = current.findIndex((note) => note.id === draggedNoteId);
    const to = current.findIndex((note) => note.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = current.splice(from, 1);
    current.splice(to, 0, moved);
    await Promise.all(
      current.map((note, index) =>
        updateDoc(doc(db, "Organizations", orgId, "Notes", note.id), {
          manualOrder: index,
          updatedAt: serverTimestamp(),
          updatedBy: userId,
        }),
      ),
    );
    setDraggedNoteId("");
    setSort("manual");
  };

  const uploadFile = async (file, kind = "attachment") => {
    if (!file || !selectedId || !orgId) return null;
    const maxBytes = kind === "image" ? IMAGE_MAX_BYTES : ATTACHMENT_MAX_BYTES;
    if (file.size > maxBytes) {
      alert.error(t("save_error"));
      return null;
    }
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "-").slice(0, 100);
      const storagePath =
        kind === "image"
          ? `notes/${orgId}/${selectedId}/images/${Date.now()}-${safeName}`
          : `notes/${orgId}/${selectedId}/attachments/${Date.now()}-${safeName}`;
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, file, { contentType: file.type || "application/octet-stream" });
      const downloadUrl = await getDownloadURL(fileRef);
      const attachment = {
        id: `${Date.now()}`,
        name: file.name,
        type: kind,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        storagePath,
        downloadUrl,
        createdAt: Date.now(),
      };
      setDraft((current) => ({
        ...(current || {}),
        attachments: [...(current?.attachments || []), attachment],
      }));
      return attachment;
    } finally {
      setUploading(false);
    }
  };

  const handleAttachmentSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    await uploadFile(file, "attachment");
  };

  const handleImageSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    const attachment = await uploadFile(file, "image");
    if (!attachment) return;
    editorRef.current?.focus();
    document.execCommand(
      "insertHTML",
      false,
      `<p><img src="${attachment.downloadUrl}" alt="${attachment.name}" /></p>`,
    );
    handleEditorInput();
  };

  const removeAttachment = async (attachment) => {
    try {
      if (attachment.storagePath) await deleteObject(ref(storage, attachment.storagePath));
    } catch (error) {
      // Ignore Storage cleanup failures; Firestore state still needs to be corrected.
    }
    setDraft((current) => ({
      ...(current || {}),
      attachments: (current?.attachments || []).filter((item) => item.id !== attachment.id),
    }));
  };

  const archiveNote = async () => {
    if (!selectedId || !orgId) return;
    await updateDoc(doc(db, "Organizations", orgId, "Notes", selectedId), {
      status: "archived",
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
    setSelectedId("");
  };

  const deleteNote = async () => {
    if (!selectedId || !orgId) return;
    await deleteDoc(doc(db, "Organizations", orgId, "Notes", selectedId));
    setSelectedId("");
  };

  const analyzeRecording = async (recorded) => {
    const { blob, durationSeconds, orgId: recordingOrgId, noteId } = recorded;
    if (!blob?.size || !noteId || !recordingOrgId || analysisInFlightRef.current) return;
    if (blob.size > NOTE_AUDIO_MAX_BYTES) {
      setAnalysisFailed(true);
      alert.error(t("audio_too_large"));
      return;
    }
    analysisInFlightRef.current = true;
    setAnalyzing(true);
    setAnalysisFailed(false);
    let stage = "upload";
    try {
      const extension = blob.type.includes("mp4") ? "mp4" : "webm";
      let audio = recorded.audio;
      if (!audio) {
        const storagePath = `notes/${recordingOrgId}/${noteId}/audio/${Date.now()}-meeting.${extension}`;
        await uploadBytes(ref(storage, storagePath), blob, {
          contentType: blob.type || "audio/webm",
        });
        audio = {
          storagePath,
          mimeType: blob.type || "audio/webm",
          size: blob.size,
          durationSeconds,
          fileName: `meeting.${extension}`,
        };
        if (isCurrentNote(recorded))
          setRecordedAudio((current) => (current ? { ...current, audio } : current));
      }
      await updateDoc(doc(db, "Organizations", recordingOrgId, "Notes", noteId), {
        meeting: {
          ...emptyMeeting,
          hasAudio: true,
          audioStoragePath: audio.storagePath,
          transcriptStatus: "pending",
          aiSummaryStatus: "pending",
          durationSeconds,
          language: "auto",
        },
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      });
      stage = "transcription";
      const result = await analyzeNoteAudio({ orgId: recordingOrgId, noteId, audio });
      if (isCurrentNote(recorded)) setSummaryPreview(result.analysis);
    } catch (error) {
      console.error("Notes audio analysis failed", { stage, code: error?.code });
      if (isCurrentNote(recorded)) {
        setAnalysisFailed(true);
        const key =
          error?.code === "AI_NOTES_TRANSCRIPTION_LIMIT"
            ? "ai_limit_reached"
            : error?.code === "AI_NOTES_AUDIO_INVALID"
              ? "audio_invalid"
              : ["AI_AUTH_REQUIRED", "AI_AUTH_INVALID", "AI_ADMIN_REQUIRED"].includes(error?.code)
                ? "audio_access_error"
                : stage === "upload"
                  ? "audio_upload_error"
                  : "audio_analysis_error";
        alert.error(t(key));
      }
    } finally {
      analysisInFlightRef.current = false;
      setAnalyzing(false);
    }
  };

  const applySummary = (mode) => {
    const html = buildSummaryHtml(summaryPreview, t);
    const nextHtml =
      mode === "replace" ? html : `${editorRef.current?.innerHTML || ""}<hr />${html}`;
    const nextTags = [
      ...new Set([...(draft?.tags || []), ...(summaryPreview.suggestedTags || [])]),
    ].slice(0, 20);
    if (editorRef.current) editorRef.current.innerHTML = nextHtml;
    updateDraft({
      title:
        draft?.title && draft.title !== t("untitled")
          ? draft.title
          : summaryPreview.titleSuggestion || t("untitled"),
      contentHtml: sanitizeHtml(nextHtml),
      contentText: stripHtml(nextHtml),
      tags: nextTags,
    });
    setTagsInput(nextTags.join(", "));
    setSummaryPreview(null);
  };

  if (!orgId) {
    return (
      <main className="notes-page">
        <div className="notes-empty-state">{t("admin_only")}</div>
      </main>
    );
  }

  return (
    <main className="notes-page">
      <section className={`notes-shell notes-mobile-${mobileView}`}>
        <aside className="notes-list-panel">
          <div className="notes-list-header">
            <div>
              <p>{getOrgName(org)}</p>
              <h1>{t("title")}</h1>
            </div>
            <button className="notes-dashboard-btn" onClick={createNote}>
              + {t("new_note")}
            </button>
          </div>
          <p className="notes-subtitle">{t("subtitle")}</p>
          <div className="notes-filters">
            <input
              value={queryText}
              onChange={(event) => setQueryText(event.target.value)}
              placeholder={t("search_placeholder")}
            />
            <button
              type="button"
              className="notes-mobile-filter-toggle"
              onClick={() => setMobileFiltersOpen((value) => !value)}
            >
              {t("filters")} <span>{mobileFiltersOpen ? "−" : "+"}</span>
            </button>
            <div className={`notes-filter-row ${mobileFiltersOpen ? "is-open" : ""}`}>
              <select value={sort} onChange={(event) => setSort(event.target.value)}>
                <option value="manual">{t("manual_order")}</option>
                <option value="recent">{t("sort_recent")}</option>
                <option value="title">{t("sort_title")}</option>
              </select>
              <select value={tagFilter} onChange={(event) => setTagFilter(event.target.value)}>
                <option value="">{t("all_tags")}</option>
                {allTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="notes-list">
            {filteredNotes.map((note) => (
              <button
                key={note.id}
                className={`notes-card ${note.id === selectedId ? "active" : ""}`}
                draggable
                onDragStart={() => setDraggedNoteId(note.id)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => reorderNotes(note.id)}
                onClick={() => selectNote(note.id)}
              >
                <span className="notes-drag-handle">⋮⋮</span>
                <strong>{note.title || t("untitled")}</strong>
                <span>{note.contentText || t("empty_editor_text")}</span>
                <small>{formatDate(note.updatedAt)}</small>
              </button>
            ))}
            {!filteredNotes.length && <div className="notes-no-results">{t("no_notes")}</div>}
          </div>
        </aside>

        <section className="notes-editor-panel">
          {!draft ? (
            <div className="notes-empty-state">
              <h2>{t("empty_editor_title")}</h2>
              <p>{t("empty_editor_text")}</p>
              <button className="notes-dashboard-btn" onClick={createNote}>
                + {t("new_note")}
              </button>
            </div>
          ) : (
            <>
              <div className="notes-editor-topbar">
                <button
                  type="button"
                  className="notes-mobile-back"
                  onClick={() => {
                    setMobileView("list");
                    setActionsOpen(false);
                  }}
                >
                  ← {t("back_to_notes")}
                </button>
                <div className={`notes-save-state ${saveState}`}>
                  {saveState === "saving"
                    ? t("saving")
                    : saveState === "error"
                      ? t("save_error")
                      : t("saved")}
                </div>
                <div className="notes-editor-actions">
                  <button onClick={exportPdf}>{t("export_pdf")}</button>
                  <button onClick={archiveNote}>{t("archive")}</button>
                  <button className="danger" onClick={deleteNote}>
                    {t("delete")}
                  </button>
                </div>
                <div className="notes-mobile-actions">
                  <button type="button" onClick={() => setActionsOpen((value) => !value)}>
                    {t("actions")} ⋯
                  </button>
                  {actionsOpen && (
                    <div className="notes-mobile-actions-menu">
                      <button type="button" onClick={() => handleAction(exportPdf)}>
                        {t("export_pdf")}
                      </button>
                      <button type="button" onClick={() => handleAction(archiveNote)}>
                        {t("archive")}
                      </button>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => handleAction(deleteNote)}
                      >
                        {t("delete")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <input
                className="notes-title-input"
                value={draft.title}
                onFocus={() => {
                  if (draft.title === t("untitled")) updateDraft({ title: "" });
                }}
                onChange={(event) => updateDraft({ title: event.target.value })}
                placeholder={t("title_placeholder")}
              />
              <div className="notes-tags-editor">
                <input
                  className="notes-tags-input"
                  value={tagsInput}
                  onChange={(event) => handleTags(event.target.value)}
                  placeholder={t("tags_placeholder")}
                />
                {!!draft.tags?.length && (
                  <div className="notes-tag-chips">
                    {draft.tags.map((tag) => (
                      <span key={tag}>#{tag}</span>
                    ))}
                  </div>
                )}
              </div>
              <section className={`notes-audio-card ${recording ? "is-recording" : ""}`}>
                <div>
                  <strong>
                    {t("meeting_recorder_title")}
                    <span className="notes-beta-badge">{t("beta")}</span>
                  </strong>
                  <p>
                    {recording
                      ? `${t("recording")} ${formatTimer(recordingSeconds)}`
                      : startingRecording
                        ? t("microphone_starting")
                        : analyzing
                          ? t("analyzing")
                          : t("meeting_recorder_text")}
                  </p>
                </div>
                <button
                  className={recording ? "notes-record-stop" : "notes-record-start"}
                  disabled={analyzing || startingRecording}
                  onClick={recording ? stopRecording : startRecording}
                >
                  {recording
                    ? t("stop_recording")
                    : startingRecording
                      ? t("microphone_starting")
                      : t("record_meeting")}
                </button>
              </section>
              {recordedAudioUrl && (
                <section className="notes-recorded-audio" aria-label={t("recorded_audio")}>
                  <audio controls src={recordedAudioUrl} aria-label={t("recorded_audio")} />
                  <a
                    href={recordedAudioUrl}
                    download={`meeting.${recordedAudio?.blob.type.includes("mp4") ? "mp4" : "webm"}`}
                  >
                    {t("download_audio")}
                  </a>
                  {analysisFailed && (
                    <button
                      type="button"
                      disabled={
                        analyzing ||
                        recording ||
                        startingRecording ||
                        recordedAudio.blob.size > NOTE_AUDIO_MAX_BYTES
                      }
                      onClick={() => analyzeRecording(recordedAudio)}
                    >
                      {t("retry_transcription")}
                    </button>
                  )}
                </section>
              )}
              {!!notes.find((note) => note.id === selectedId)?.meeting?.transcriptTextPreview && (
                <details className="notes-transcript-preview">
                  <summary>{t("transcript_preview")}</summary>
                  <p>
                    {notes.find((note) => note.id === selectedId).meeting.transcriptTextPreview}
                  </p>
                </details>
              )}
              <div
                className="notes-toolbar notes-desktop-toolbar"
                onMouseDown={saveEditorSelection}
              >
                {renderToolbarControls("desktop")}
              </div>
              <input ref={fileInputRef} type="file" hidden onChange={handleAttachmentSelected} />
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageSelected}
              />
              {(uploading || analyzing) && (
                <div className="notes-processing">
                  <span />
                  {analyzing ? t("analyzing") : t("saving")}
                </div>
              )}
              <div
                ref={editorRef}
                className="notes-editor"
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditorInput}
                onKeyDown={handleEditorKeyDown}
                onSelect={saveEditorSelection}
                onMouseUp={saveEditorSelection}
                onTouchEnd={saveEditorSelection}
                onKeyUp={saveEditorSelection}
                onBlur={saveEditorSelection}
                data-placeholder={t("empty_editor_text")}
              />
              <aside className="notes-attachments">
                <h3>{t("attachments")}</h3>
                {!draft.attachments?.length && <p>{t("no_attachments")}</p>}
                {draft.attachments?.map((attachment) => (
                  <div className="notes-attachment" key={attachment.id}>
                    <a href={attachment.downloadUrl} target="_blank" rel="noreferrer">
                      {attachment.name}
                    </a>
                    <button onClick={() => removeAttachment(attachment)}>×</button>
                  </div>
                ))}
              </aside>
              <div className="notes-toolbar notes-mobile-toolbar" onMouseDown={saveEditorSelection}>
                {mobileColorPanelOpen && (
                  <div className="notes-mobile-format-popover notes-mobile-color-popover">
                    <span>{t("text_color")}</span>
                    <div>
                      {TEXT_COLOR_PRESETS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className="notes-color-dot"
                          aria-label={`${t("text_color")} ${color}`}
                          style={{ "--note-color": color }}
                          onPointerDown={preserveSelectionPointer}
                          onClick={() => {
                            setTextColor(color);
                            setMobileColorPanelOpen(false);
                          }}
                        />
                      ))}
                      <input
                        type="color"
                        defaultValue="#ffffff"
                        onMouseDown={saveEditorSelection}
                        onChange={(event) => setTextColor(event.target.value)}
                      />
                    </div>
                  </div>
                )}
                {mobileSizePanelOpen && (
                  <div className="notes-mobile-format-popover notes-mobile-size-popover">
                    <span>{t("text_size")}</span>
                    <div>
                      {TEXT_SIZE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className="notes-size-option"
                          onPointerDown={preserveSelectionPointer}
                          onClick={() => {
                            setTextSize(option.value);
                            setMobileSizePanelOpen(false);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {renderToolbarControls("mobile")}
              </div>
            </>
          )}
        </section>
      </section>

      {summaryPreview && (
        <div className="notes-modal-backdrop">
          <section className="notes-summary-modal">
            <h2>{t("summary_preview_title")}</h2>
            <h3>{summaryPreview.titleSuggestion}</h3>
            <p>{summaryPreview.summary}</p>
            <div className="notes-summary-grid">
              <div>
                <strong>{t("key_points")}</strong>
                <ul>
                  {summaryPreview.keyPoints?.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>{t("decisions")}</strong>
                <ul>
                  {summaryPreview.decisions?.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>{t("actions")}</strong>
                <ul>
                  {summaryPreview.actionItems?.map((item, index) => (
                    <li key={`${item.task}-${index}`}>{item.task}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>{t("questions")}</strong>
                <ul>
                  {summaryPreview.openQuestions?.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="notes-modal-actions">
              <button onClick={() => setSummaryPreview(null)}>{t("close")}</button>
              <button onClick={() => applySummary("replace")}>{t("replace_content")}</button>
              <button className="notes-dashboard-btn" onClick={() => applySummary("insert")}>
                {t("insert_summary")}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default NotesPage;
